import React, { useEffect } from "react";
import {
  Card,
  Col,
  Flex,
  Form,
  InputNumber,
  Radio,
  Row,
  Typography,
  Button,
  Select,
  Avatar,
  Input,
} from "antd";
import { useTranslation } from "react-i18next";
import { colors } from "../../../../../../constants/colors";
import RequiredLabel from "../../../../../common/required-label/RequiredLabel";
import { useState } from "react";
import { getWalletInfoAction } from "../../../../../../redux/services/wallet-info/walletInfoServices";
import { useDispatch, useSelector } from "react-redux";
import { getStaffSelectListAction } from "../../../../../../redux/services/staff/staffServices";
import useLocalStorage from "../../../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../../../constants/config/appConfig";
import { routes } from "../../../../../../constants";
import Actions from "../../../../../../redux/actions";
import { baseRouteMaker } from "../../../../../../constants/routeConsts";
import { useNavigate } from "react-router-dom";
import { searchPersonAction } from "../../../../../../redux/services/common/commonServices";
import { allowCharactersOnlyForIntegerNumbers } from "../../../../../../utils/appKeyStrokeLimitingUtils";
import { UserOutlined } from "@ant-design/icons";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";

const WalletInfoPaymentForm = () => {
  let timeout = null;

  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* navigate dependencies */
  const navigate = useNavigate();
  /* navigate dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* local storage dependencies */
  const { userSession } = useLocalStorage(appConfig.SESSION_USER_INFO);
  const { loginDetails } = useSelector((state) => state.auth);
  const [searchPersonSelectList, setSearchPersonSelectList] = useState([]);
  /* local storage dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  const walletBalanceFor = Form.useWatch("wallet_balance_for", {
    form,
    preserve: true,
  });
  const staff = Form.useWatch("staff", {
    form,
    preserve: true,
  });
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: 500,
  });
  /* form dependencies */

  /* table dependencies */
  const { staffSelectList } = useSelector((state) => state.staff);
  const { walletInfo } = useSelector((state) => state.walletInfo);
  /* table dependencies */

  const selectListApiCalls = () => {
    const dataToBeSentForStaffSelectList = { page: 1, limit: 500 };
    // service provider / staff select list
    dispatch(
      getStaffSelectListAction(dataToBeSentForStaffSelectList, setLoading)
    );
  };

  /* on customer search */
  const onCustomerSearch = (searchValue) => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }

    const searchCustomers = () => {
      const dataToBeSent = {
        page: pagination?.pageNo,
        limit: pagination?.pageSize,
        search: "",
        searchByUsername : searchValue
      };

      dispatch(searchPersonAction(dataToBeSent, setSelectListLoader)).then(
        (apiRes) => {
          const customers = apiRes?.data;
          setSearchPersonSelectList(
            customers?.map((element) => ({
              ...element,
              value: element?._id,
              label: `${element?.first_name} ${element?.last_name}`,
            }))
          );
        }
      );
    };

    if (searchValue) {
      timeout = setTimeout(searchCustomers, 500);
    } else {
      setSearchPersonSelectList([]);
    }
  };
  /* on customer search */

  useEffect(() => {
    selectListApiCalls();
  }, []);

  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      page: 1,
      limit: 500
    };
    dispatch(getWalletInfoAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, []);

  const onCustomerChanged = (value) => {};

  /* on submit */
  const onSubmit = (values) => {
    const dataToBeSent = {
      type: "main",
      event_type: "ORDER_PAYMENT",
      order_id: null,
      amount: Number(values?.amount),
      currency: "aed",
      to_id:
        walletBalanceFor === "my_friend" ? values?.staff : loginDetails?._id,
    };

    dispatch(
      Actions.createAction(Actions.SELECTED_WALLET_PLAN_DETAILS, {
        selectedWalletInfoPlanDetails: dataToBeSent,
      })
    );
    navigate(baseRouteMaker(routes.WALLET_PAYMENT));
  };
  /* on submit */

  return (
    <Form
      form={form}
      layout="vertical"
      autoComplete="off"
      requiredMark={false}
      onFinish={onSubmit}
    >
      <Flex vertical gap="1rem">
        <Row gutter={24}>
          <Col span={7}>
            <Card bordered hoverable 
            style={{
              width:'100%',
              padding: 0,
              borderRadius: 20,
              border: `1px solid ${colors.oceanBlue}`,
            }}
            >
              <Typography.Title level={3}>
                {t("walletInfo.balance")}
              </Typography.Title>
              <Typography.Title
                level={1}
                style={{ margin: "10px 0", color: colors.electricBlue }}
              >
                {walletInfo?.wallet?.wallet_balance?.toFixed(2) || 0}{" "}
                {t("walletInfo.points")}
              </Typography.Title>
            </Card>
          </Col>
        </Row>
        <Row gutter={[24, 24]}>
          <Col span={8}>
            <Form.Item
              name="wallet_balance_for"
              label={
                <RequiredLabel
                  text={t("Modules.WalletInfo.walletBalanceFor")}
                  required={true}
                />
              }
              rules={[
                {
                  required: true,
                  message: t("Modules.WalletInfo.pleaseSelectWalletBalanceFor"),
                },
              ]}
            >
              <Radio.Group
                options={[
                  { label: t("walletInfo.self"), value: "self" },
                  { label: t("walletInfo.friend"), value: "my_friend" },
                ]}
                onChange={() => {
                  setSearchPersonSelectList([]);
                  form.setFieldValue("staff", null);
                }}
              />
            </Form.Item>
          </Col>
          {walletBalanceFor === "my_friend" && (
            <Col span={8}>
              <Form.Item
                name="staff"
                label={
                  <RequiredLabel
                    text={t("Modules.Appointments.customerUserName")}
                    required={true}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: t("Modules.Appointments.pleaseSelectCustomerUserName"),
                  },
                ]}
              >
                <Select
                  showSearch
                  loading={selectListLoader}
                  placeholder={t("Modules.Appointments.searchByCustomerUserName")}
                  notFoundContent={null}
                  filterOption={false}
                  defaultActiveFirstOption={false}
                  onSearch={onCustomerSearch}
                  onChange={onCustomerChanged}
                  allowClear
                  onClear={() => setSearchPersonSelectList([])}
                  
                >
                  {searchPersonSelectList &&
                    searchPersonSelectList?.map((item) => (
                      <Select.Option key={item?.value} value={item?.value}>
                        <Row gutter={[24, 24]} align="middle">
                          <Col span={3}>
                            <Avatar
                              size="default"
                              src={item?.profile_image}
                              icon={<UserOutlined />}
                            />
                          </Col>
                          <Col span={21}>
                            <Flex vertical>
                              <Flex align="center">
                                <Typography.Title
                                  level={5}
                                  style={{ color: colors.electricBlue }}
                                >
                                  {`${item?.label} - ${item?.username}` || "-"}
                                </Typography.Title>
                              </Flex>
                              <Typography.Title level={5} style={{ margin: 0 }}>
                              {item?.email || "-"}
                            </Typography.Title>
                            </Flex>
                          </Col>
                        </Row>
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col span={8}>
            <Form.Item
              label={
                <RequiredLabel
                  text={t("Modules.WalletInfo.amount")}
                  required={true}
                />
              }
              name="amount"
              rules={[
                {
                  required: true,
                  message: t("Modules.WalletInfo.pleaseEnterAmount"),
                },
                {
                  validator: (_, value) => {
                    if (value && value < 1)
                      return Promise.reject(
                        `${t("Modules.WalletInfo.amount")} ${t(
                          "CommonUtils.shouldBeGreaterThan0"
                        )}`
                      );
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                type="number"
                placeholder={t("Modules.WalletInfo.enterAmount")}
                onKeyDown={(e) => allowCharactersOnlyForIntegerNumbers(e)}
              />
            </Form.Item>
          </Col>

          <Col span={24} style={{ textAlign: "center" }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={loading}
            >
              {t("CommonUtils.add")}
            </Button>
          </Col>
        </Row>
      </Flex>
    </Form>
  );
};

export default WalletInfoPaymentForm;
